import { Component, Input, OnChanges, SimpleChanges, SecurityContext } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { AudienceService } from '../../services/audience.service';

/**
 * Represents a blog author with optional bio and avatar
 */
export interface BlogAuthor {
  name: string;
  slug?: string;
  authorId?: string;
  bio?: string;
  avatarUrl?: string;
  title?: string;
}

/**
 * Represents a single author reference from Builder.io (reference type)
 */
export interface AuthorReference {
  '@type'?: string;
  id?: string;
  model?: string;
  value?: {
    data?: {
      name?: string;
      slug?: string;
      authorId?: string;
      bio?: string;
      headshot?: string;
      title?: string;
    };
  };
}

/**
 * Represents a taxonomy reference from Builder.io
 * Different models may use different field names (name, title, coursename)
 */
export interface TaxonomyReference {
  '@type'?: string;
  id?: string;
  model?: string;
  value?: {
    data?: {
      name?: string;
      title?: string;
      coursename?: string;
    };
    name?: string;
  };
}

/**
 * Represents a topic-theme pair from Builder.io
 */
export interface TopicThemePair {
  topic?: TaxonomyReference;
  theme?: TaxonomyReference;
}

/**
 * Represents a taxonomy tag with its category for display
 */
export interface TaxonomyTag {
  name: string;
  category: string;
}

/**
 * Represents blog article data from Builder.io
 */
export interface BlogData {
  title?: string;
  slug?: string;
  featuredImageUrl?: string;
  excerpt?: string;
  content?: string;
  authors?: AuthorReference; // Field name is "authors" but it's a single reference now
  publishedDate?: string;
  tags?: string[];
  status?: string;
  // Taxonomy fields
  product?: string;
  discipline?: TaxonomyReference;
  subject?: TaxonomyReference;
  contentSeries?: TaxonomyReference;
  // Topic-Theme pairs (new structure)
  topicthemepairs?: TopicThemePair[];
  // Legacy fields (kept for backward compatibility)
  topic?: TaxonomyReference | string;
  theme?: TaxonomyReference | string;
}

/**
 * BlogArticleComponent - Displays blog article content with taxonomy tags
 * 
 * Renders article header, body, taxonomy chips, and author details
 */
@Component({
  selector: 'app-blog-article',
  templateUrl: './blog-article.component.html',
  styleUrls: ['./blog-article.component.scss'],
  standalone: true,
  imports: [CommonModule, RouterModule]
})
export class BlogArticleComponent implements OnChanges {
  @Input() title: string = '';
  @Input() excerpt?: string;
  @Input() publishedDate?: string;
  @Input() author?: BlogAuthor; // Changed from authors array to single author
  @Input() heroImageUrl?: string;
  @Input() bodyHtml?: string;
  
  // Taxonomy inputs
  @Input() discipline?: string;
  @Input() topics: string[] = [];
  @Input() themes: string[] = [];
  @Input() product?: string;
  @Input() contentSeries?: string;
  @Input() subject?: string; // BFW/high school only

  // Cache the sanitized HTML to prevent flickering
  safeBodyHtml: SafeHtml = '';

  constructor(
    private sanitizer: DomSanitizer,
    private route: ActivatedRoute,
    private audienceService: AudienceService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    // Only re-sanitize when bodyHtml actually changes
    if (changes['bodyHtml'] && this.bodyHtml) {
      this.safeBodyHtml = this.sanitizeHtmlWithYoutubeVideos(this.bodyHtml);
    }
  }

  /**
   * Sanitize HTML but preserve youtube-video tags
   * This is more secure than bypassing all sanitization
   */
  private sanitizeHtmlWithYoutubeVideos(html: string): SafeHtml {
    // First, remove empty table rows
    let processedHtml = this.removeEmptyTableRows(html);

    // Extract youtube-video tags with placeholders
    const youtubeVideos: string[] = [];
    const placeholder = '___YOUTUBE_VIDEO_PLACEHOLDER___';
    
    // Replace youtube-video tags with placeholders
    processedHtml = processedHtml.replace(
      /<youtube-video[^>]*src="([^"]*)"[^>]*>[\s\S]*?<\/youtube-video>/gi,
      (match, src) => {
        youtubeVideos.push(match);
        return `${placeholder}${youtubeVideos.length - 1}${placeholder}`;
      }
    );

    // Let Angular sanitize the rest of the HTML (this removes any malicious code)
    const sanitizedHtml = this.sanitizer.sanitize(SecurityContext.HTML, processedHtml) || '';

    // Re-insert youtube-video tags after sanitization
    let finalHtml = sanitizedHtml;
    youtubeVideos.forEach((videoTag, index) => {
      const placeholderToReplace = `${placeholder}${index}${placeholder}`;
      finalHtml = finalHtml.replace(placeholderToReplace, videoTag);
    });

    // Now bypass sanitization only for the final HTML that we've manually processed
    return this.sanitizer.bypassSecurityTrustHtml(finalHtml);
  }

  /**
   * Removes empty table rows from HTML content
   * A row is considered empty if all its cells contain only whitespace or &nbsp;
   */
  private removeEmptyTableRows(html: string): string {
    return html.replace(/<tr[^>]*>([\s\S]*?)<\/tr>/gi, (match, rowContent) => {
      // Extract all cell contents (td and th)
      const cellContents = rowContent.match(/<t[dh][^>]*>([\s\S]*?)<\/t[dh]>/gi) || [];
      
      // Check if ALL cells are empty
      const allCellsEmpty = cellContents.every((cell: string) => {
        // Extract content between tags
        const content = cell.replace(/<t[dh][^>]*>([\s\S]*?)<\/t[dh]>/i, '$1');
        // Remove HTML tags, &nbsp;, and whitespace, then check if empty
        const textContent = content
          .replace(/<[^>]*>/g, '')
          .replace(/&nbsp;/gi, '')
          .replace(/&#160;/gi, '')
          .replace(/\u00A0/g, '')  // Non-breaking space character
          .replace(/\s+/g, '')
          .trim();
        return textContent.length === 0;
      });

      return allCellsEmpty && cellContents.length > 0 ? '' : match;
    });
  }

  /**
   * Formats the published date to "Month Day, Year" format (e.g., "December 12, 2025")
   */
  get formattedPublishedDate(): string {
    if (!this.publishedDate) return '';
    
    try {
      const date = new Date(this.publishedDate);
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return this.publishedDate; // Return original if invalid
      }
      
      const options: Intl.DateTimeFormatOptions = { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      };
      
      return date.toLocaleDateString('en-US', options);
    } catch (error) {
      return this.publishedDate; // Fallback to original value
    }
  }

  /**
   * Returns an array of taxonomy tags with their categories for display
   */
  get taxonomyTags(): TaxonomyTag[] {
    const tags: TaxonomyTag[] = [];
    
    if (this.discipline) {
      tags.push({ name: this.discipline, category: 'discipline' });
    }
    
    // Add all topics
    if (this.topics && this.topics.length > 0) {
      this.topics.forEach(topic => {
        tags.push({ name: topic, category: 'topic' });
      });
    }
    
    // Add all themes
    if (this.themes && this.themes.length > 0) {
      this.themes.forEach(theme => {
        tags.push({ name: theme, category: 'theme' });
      });
    }
    
    if (this.product) {
      tags.push({ name: this.product, category: 'product' });
    }
    
    if (this.contentSeries) {
      tags.push({ name: this.contentSeries, category: 'contentSeries' });
    }
    
    // Add subject (BFW/high school only)
    if (this.subject) {
      tags.push({ name: this.subject, category: 'subject' });
    }
    
    return tags;
  }

  /**
   * Gets the base path for content hub routes based on current audience
   * Returns /content-hub/highered or /content-hub/highschool
   */
  private getBasePath(): string {
    const audience = this.audienceService.audience || this.getAudience();
    return `/${audience}`;
  }

  /**
   * Gets the current audience from route or service
   */
  private getAudience(): string {
    // Try to get audience from route params (parent or grandparent route)
    const audience = this.route.parent?.snapshot.paramMap.get('audience') || 
                     this.route.parent?.parent?.snapshot.paramMap.get('audience') ||
                     this.audienceService.audience;
    return audience;
  }

  /**
   * Determines if a taxonomy chip should be clickable (has a defined destination)
   */
  isChipClickable(tag: TaxonomyTag): boolean {
    switch (tag.category) {
      case 'topic':
      case 'contentSeries':
      case 'discipline':
        return true;
      case 'subject':
        // Subject chips are clickable only if subject listing page exists
        // For now, we'll check if we're on BFW/highschool and assume the page exists
        // This can be enhanced later to check if the route actually exists
        return this.audienceService.isHighSchool();
      case 'theme':
        // Theme chips → Topic Page with topic=All&theme params
        return true;
      default:
        return false;
    }
  }

  /**
   * Gets the route path for a taxonomy chip (without query params)
   */
  getChipRoute(tag: TaxonomyTag): string | null {
  if (!this.isChipClickable(tag)) {
    return null;
  }

  const basePath = this.getBasePath();

  switch (tag.category) {
    case 'topic':
      // ✅ FIX: include basePath
      return `${basePath}/topic`;
    
    case 'contentSeries':
      return `${basePath}/content-series`;
    
    case 'discipline':
      // ✅ FIX: include basePath
      return `${basePath}/topic`;
    
    case 'theme':
      // ✅ FIX: include basePath
      return `${basePath}/topic`;
    
    case 'subject':
      if (this.audienceService.isHighSchool()) {
        return `${basePath}/subject`;
      }
      return null;
    
    default:
      return null;
  }
}

  /**
   * Gets the query parameters for a taxonomy chip
   */
  getChipQueryParams(tag: TaxonomyTag): { [key: string]: string } | null {
    if (!this.isChipClickable(tag)) {
      return null;
    }

    switch (tag.category) {
      case 'topic':
        return { topic: tag.name };
      
      case 'contentSeries':
        return { series: tag.name };
      
      case 'discipline':
        // Discipline chips → Topic Page with topic=All&discipline query params
        return { topic: 'All', discipline: tag.name };
      
      case 'theme':
        // Theme chips → Topic Page with topic=All&theme query params
        return { topic: 'All', theme: tag.name };
      
      case 'subject':
        return { subject: tag.name };
      
      default:
        return null;
    }
  }
}